import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, ClipboardPaste, Wifi, Hand, Trash2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg, img, tl } from "@/lib/api";
import { PageTitle } from "@/pages/admin/AdminLayout";

const Badge = ({ source }) =>
  source === "online" ? (
    <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded bg-green-100 text-green-800"><Wifi className="h-3 w-3" />ONLINE</span>
  ) : (
    <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-800"><Hand className="h-3 w-3" />MANUEL</span>
  );

function ManualDialog({ open, onClose, onSaved, banks }) {
  const blank = { amount: "", payer_name: "", bank: banks[0] || "", description: "", order_no: "", date: "", receipt_base64: "" };
  const [f, setF] = useState(blank);
  const [reading, setReading] = useState(false);
  const parse = async (dataUrl) => {
    setReading(true);
    try {
      const { data } = await api.post("/admin/payments/parse-receipt", { image_base64: dataUrl });
      setF((x) => ({
        ...x,
        amount: data.amount ?? x.amount,
        payer_name: data.payer_name || x.payer_name,
        description: data.description || x.description,
        bank: data.bank || x.bank,
        order_no: data.order_no || x.order_no,
        date: /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(data.date) ? data.date.slice(0, 16) : x.date,
      }));
      toast.success("Dekont okundu, bilgileri kontrol edin");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setReading(false);
    }
  };
  const readFile = (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    const r = new FileReader();
    r.onload = () => {
      setF((x) => ({ ...x, receipt_base64: r.result }));
      parse(r.result);
    };
    r.readAsDataURL(file);
  };
  const onPaste = (e) => {
    const item = [...(e.clipboardData?.items || [])].find((i) => i.type.startsWith("image/"));
    if (item) {
      e.preventDefault();
      readFile(item.getAsFile());
      toast.success("Dekont görüntüsü yapıştırıldı");
    }
  };
  const save = async () => {
    try {
      await api.post("/admin/payments/manual", { ...f, amount: Number(f.amount), date: f.date ? new Date(f.date).toISOString() : "" });
      toast.success("Manuel ödeme eklendi");
      setF(blank);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" onPaste={onPaste} data-testid="manual-payment-dialog">
        <DialogHeader>
          <DialogTitle>Manuel Dekont / Ödeme Ekle</DialogTitle>
          <DialogDescription>Dekontun ekran görüntüsünü Ctrl+V ile yapıştırın veya dosya seçin; tutar, gönderen ve açıklama otomatik doldurulur.</DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <div data-testid="receipt-paste-area" tabIndex={0} className="sm:row-span-3 border-2 border-dashed rounded-lg min-h-[220px] flex flex-col items-center justify-center text-center p-3 bg-slate-50 focus:border-[#172554] outline-none">
            {f.receipt_base64 ? <img src={f.receipt_base64} alt="Dekont" className="max-h-64 object-contain" /> : (
              <><ClipboardPaste className="h-8 w-8 text-slate-400" /><p className="text-sm text-slate-500 mt-2">Buraya tıklayıp Ctrl+V ile yapıştırın</p></>
            )}
            <input data-testid="receipt-file-input" type="file" accept="image/*" onChange={(e) => readFile(e.target.files[0])} className="mt-3 text-xs" />
            {reading && <div data-testid="receipt-reading" className="mt-2 text-xs font-semibold text-[#172554] flex items-center gap-1"><Loader2 className="h-3.5 w-3.5 animate-spin" />Dekont okunuyor...</div>}
          </div>
          <label className="text-sm"><span className="font-semibold">Tutar (TL)</span><input data-testid="manual-amount" type="number" step="0.01" value={f.amount} onChange={set("amount")} className="mt-1 w-full h-10 border rounded-md px-3" /></label>
          <label className="text-sm"><span className="font-semibold">Gönderen ad soyad</span><input data-testid="manual-payer" value={f.payer_name} onChange={set("payer_name")} className="mt-1 w-full h-10 border rounded-md px-3" /></label>
          <label className="text-sm"><span className="font-semibold">Banka</span>
            <input data-testid="manual-bank" list="bank-list" value={f.bank} onChange={set("bank")} className="mt-1 w-full h-10 border rounded-md px-3" />
            <datalist id="bank-list">{banks.map((b) => <option key={b} value={b} />)}</datalist>
          </label>
          <label className="text-sm"><span className="font-semibold">Dekont açıklaması</span><input data-testid="manual-description" value={f.description} onChange={set("description")} placeholder="Müşterinin yazdığı açıklama" className="mt-1 w-full h-10 border rounded-md px-3" /></label>
          <label className="text-sm"><span className="font-semibold">Sipariş no (varsa)</span><input data-testid="manual-order-no" value={f.order_no} onChange={set("order_no")} placeholder="OS..." className="mt-1 w-full h-10 border rounded-md px-3" /></label>
          <label className="text-sm"><span className="font-semibold">Ödeme tarihi</span><input data-testid="manual-date" type="datetime-local" value={f.date} onChange={set("date")} className="mt-1 w-full h-10 border rounded-md px-3" /></label>
        </div>
        <p className="text-xs text-slate-500">Sipariş no yazarsanız ilgili sipariş otomatik "Ödendi" olarak işaretlenir.</p>
        <DialogFooter><button data-testid="manual-save-button" onClick={save} className="h-10 px-5 rounded-md bg-[#16A34A] text-white font-bold">Kaydet</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminPayments() {
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["admin-payments"], queryFn: () => api.get("/admin/payments").then((r) => r.data) });
  const { data: s } = useQuery({ queryKey: ["admin-settings"], queryFn: () => api.get("/admin/settings").then((r) => r.data) });
  const [open, setOpen] = useState(false);
  const [f, setF] = useState("all");
  const [preview, setPreview] = useState(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-payments"] });
  const list = data.filter((p) => f === "all" || p.source === f);
  const sum = (src) => data.filter((p) => !src || p.source === src).reduce((a, p) => a + p.amount, 0);
  const remove = async (p) => {
    await api.delete(`/admin/payments/${p.id}`);
    toast.success("Silindi");
    refresh();
  };

  return (
    <>
      <PageTitle title="Gelen Ödemeler" desc="Banka entegrasyonundan gelenler ONLINE, sizin eklediğiniz dekontlar MANUEL olarak listelenir.">
        <button data-testid="add-manual-payment-button" onClick={() => setOpen(true)} className="h-10 px-4 rounded-md bg-[#172554] text-white font-bold flex items-center gap-2"><Plus className="h-4 w-4" />Manuel Dekont Ekle</button>
      </PageTitle>
      <div className="grid grid-cols-3 gap-3 mb-4">
        {[["all", "Toplam", sum()], ["online", "Online (Banka/Kart)", sum("online")], ["manual", "Manuel (Dekont)", sum("manual")]].map(([k, l, v]) => (
          <button key={k} data-testid={`payments-filter-${k}`} onClick={() => setF(k)} className={`text-left bg-white border rounded-lg p-4 ${f === k ? "ring-2 ring-[#172554]" : ""}`}>
            <div className="text-xs text-slate-500">{l}</div><div className="text-lg sm:text-2xl font-extrabold text-[#172554]">{tl(v)}</div>
          </button>
        ))}
      </div>
      <div className="bg-white border rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="payments-table">
          <thead className="bg-slate-50 text-xs text-slate-600"><tr>{["Tarih", "Kaynak", "Banka / Sağlayıcı", "Gönderen", "Açıklama", "Sipariş", "Tutar", "Dekont", ""].map((h) => <th key={h} className="text-left p-3 whitespace-nowrap">{h}</th>)}</tr></thead>
          <tbody>
            {list.map((p) => (
              <tr key={p.id} className="border-b" data-testid={`payment-row-${p.id}`}>
                <td className="p-3 whitespace-nowrap text-xs">{new Date(p.date).toLocaleString("tr-TR")}</td>
                <td className="p-3"><Badge source={p.source} /></td>
                <td className="p-3">{p.bank}</td>
                <td className="p-3 font-semibold">{p.payer_name}</td>
                <td className="p-3 text-xs max-w-[220px]">{p.description}</td>
                <td className="p-3 font-mono text-xs">{p.order_no || "-"}</td>
                <td className="p-3 font-extrabold text-[#15803D] whitespace-nowrap">{tl(p.amount)}</td>
                <td className="p-3">{p.receipt_image ? <button onClick={() => setPreview(p.receipt_image)}><img src={img(p.receipt_image)} alt="dekont" className="h-10 w-10 object-cover rounded border" /></button> : "-"}</td>
                <td className="p-3">{p.source === "manual" && <button data-testid={`payment-delete-${p.id}`} onClick={() => remove(p)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!list.length && <div className="p-10 text-center text-slate-500">Henüz ödeme kaydı yok.</div>}
      </div>
      <ManualDialog open={open} onClose={() => setOpen(false)} onSaved={refresh} banks={(s?.ibans || []).map((b) => b.bank)} />
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl"><DialogHeader><DialogTitle>Dekont</DialogTitle><DialogDescription>Müşteri dekontu</DialogDescription></DialogHeader>{preview && <img src={img(preview)} alt="dekont" className="w-full object-contain max-h-[75vh]" />}</DialogContent>
      </Dialog>
    </>
  );
}
